import { BaseZenputCollection, BaseZenputModel } from '../../../backbone-unorganized/shared/forms';
import { copyFileToAppDirectory } from '../../../backbone-unorganized/utils/files';
import { CordovaPlugins } from '../../../shared/CordovaPlugins';
import { Capacitor } from '@capacitor/core';
import { isAndroid, isIos, isNative, isWeb, isWindows } from '../../../shared/utils/is';
import { showUploadProgress } from '../../../shared/utils/notifications';
import { getDebuggingFlag } from '../../../shared/utils/debugging';
import { base64UrlToBlob, blob2base64 } from '../../../shared/utils/files';
/**
 * A single wrapper for file objects in mobile web and Cordova.
 */
export const ZenputFile = BaseZenputModel.extend({
    // This is what this.get('raw_file') looks like on Cordova
    // {
    //   "isFile": true,
    //   "isDirectory": false,
    //   "name": "zp-2-1534467313719.jpg",
    //   "fullPath": "/zp-2-1534467313719.jpg",
    //   "filesystem": "<FileSystem: files>",
    //   "nativeURL": "file:///data/user/0/com.zenput.mobile/files/zp-2-1534467313719.jpg" // This is an Android URL
    // }

    defaults: {
        id: null, // unique ID for this file, generated by us
        raw_file: null, // a Cordova FileEntry or a web File
        mimetype: '', // video/mp4 or image/jpg
        metadata: null, // used by native to store extra info such as name or size
        url_base64: null, // used by mobile web
        url_native: '', // the path of the image on disk as a file:// path. This is the same as
        // raw_file.nativeURL on native, and also what Zencam sends us.
        // WARNING: on iOS, the app's data directory path changes, so this will become
        // outdated after a reinstall/App Store update. @jira:FORMS-365
        url_cdva: null, // The cdva:// URL, as generated by cdva_file.toURL()
        url_s3: '', // the path of an already uploaded image
        original_url_native: '', // path of file before it was edited (or before it was copied into our directory)
        legacy_file_structure: null, // when upgrading from old apps with legacy file structures, we wil store the
        // original here, just in case we need to roll back. @jira:MOBILE-727
        // @todo @jira:ZP-3048 These belong in metadata, but they are being removed from there somehow
        size: null, // original filesize
        path: null, // file url,
        indexed_db_id: null, // id of the attachment in IndexedDB
        laplacian_variance: null, // image quality value
        is_low_quality: null // whether user chose to ignore our warning about their photo being low-quality
    },

    constructor: function (attributes, options) {
        // Added August 2018: if we are upgrading from an older app, we need to convert the legacy file structure to
        // something this model understands. @jira:MOBILE-727

        if (attributes && (attributes.uuid || attributes.raw)) {
            // is legacy file/signature
            var raw_file = null;
            if (attributes.raw) {
                // if signature field
                raw_file = {
                    name: attributes.name,
                    raw: attributes.raw
                };
            } else {
                // image/video field
                raw_file = attributes;
            }

            attributes = {
                id: attributes.uuid || attributes.name, // signatures didn't have uuid's, so use the name if no uuid
                raw_file: raw_file,
                mimetype: attributes.type,
                metadata: attributes.metadata,
                url_native: attributes.nativeURL,
                url_cdva: attributes.url,
                url_base64: raw_file.raw ? `data:${attributes.type},${raw_file.raw}` : null,
                legacy_file_structure: attributes, // keep this around, just in case we need to roll back,
                laplacian_variance: attributes.laplacian_variance,
                is_low_quality: attributes.is_low_quality
            };
        }

        ZenputFile.__super__.constructor.call(this, attributes, options);
    },

    initialize: function (attributes, options) {
        if (isNative()) {
            // for iOS, correct any outdated native URLs
            this.set('url_native', this.get_url_native(), { silent: true });
        }
    },

    get_url_native: function () {
        if (!isIos() || !this.get('raw_file')) {
            return this.get('url_native'); // don't have a file obj yet, so this must be our file path @jira:FORMS-365
        }

        // Scoped Storage on Android (10+) prefers cdvfile:// content URIs rather than absolute file paths
        if (isAndroid() && this.get('url_cdva')) {
            return this.get('url_cdva');
        }

        return `${CordovaPlugins().file.dataDirectory}${this.get('raw_file').name}`;
    },

    /**
     * Get the display URL for the file.
     *
     * @param  {boolean} get_thumbnailed    If true, get the thumbnailed version of the file.
     * @param {boolean} is_draft            Is this a draft (default=false)
     * @return {string}                     A URL that can be displayed to show the file as an image
     */
    get_display_url: function (get_thumbnailed, is_draft) {
        is_draft = is_draft || false;

        if (this.get('raw_file')) {
            // we're on native
            if (isWeb()) {
                return this.get('url_base64');
            }

            // for non-thumbnails, we send the file to zencam, which needs the file://. But thumbnails that load
            // in the browser need to remove it to work with Ionic WKWebview's localhost.
            if (get_thumbnailed && !isWindows()) {
                // This won't work on Android when using --live since it relies on WEBVIEW_SERVER_URL
                // for --live development the value is http://<ip>:<port>/mobile which results in an incorrect path.
                return Capacitor.convertFileSrc(this.get_url_native());
            }

            return this.get_url_native();
        }

        var url_s3 = $f.storage.get_storage_full_url(this.get('url_s3'));

        // we don't use thumbnails for drafts b/c the server only processes the thumbnails upon submission.
        if (get_thumbnailed && !is_draft) {
            return $f.storage.get_thumbnail(url_s3, '234');
        }

        return url_s3;
    },

    /**
     * This is copied from cordova-plugin-ionic-webview's WebView.convertFileSrc:
     * https://github.com/ionic-team/cordova-plugin-ionic-webview/blob/ab7dfc06a7f8780f75800dae4f4217d1956aa7b6/src/www/util.js
     *
     * This converts a file:// URL (used by Zencam, etc.) --> a zenput:// URL that can be loaded in the DOM on iOS
     */
    convert_file_src_for_ios: (url) => {
        if (!url) {
            return url;
        }

        const ios_webview_server_url = API_URL.replace(/https?:\/\//, 'zenput://');

        if (url.indexOf('/') === 0) {
            return ios_webview_server_url + '/_app_file_' + url;
        }

        if (url.indexOf('file://') === 0) {
            return ios_webview_server_url + url.replace('file://', '/_app_file_');
        }

        if (url.indexOf('content://') === 0) {
            return ios_webview_server_url + url.replace('content:/', '/_app_content_');
        }

        return url;
    },

    /**
     * Process the file to be used by Zenput. For native, this will perform validation checks on the path, copy the file
     * to our app directory, and more. For web, we'll load the image so we can display it as a thumbnail.
     *
     * @param  {Function} callback The callback, which will provide a FileEntry object or null.
     */
    process_for_zenput: function (success, error) {
        if (isWeb()) {
            this._process_web().then(success).catch(error);
        } else {
            this._process_native(success, error);
        }
    },

    _process_web: async function () {
        const fileObject = this.get('raw_file');
        this.set(
            {
                size: this.get('raw_file').size,
                // serialize File object so we can store it in IndexedDb and use it later when uploading to S3
                raw_file: {
                    lastModified: fileObject.lastModified,
                    lastModifiedDate: fileObject.lastModifiedDate,
                    name: fileObject.name,
                    size: fileObject.size,
                    type: fileObject.type
                },
                url_base64: await blob2base64(this.get('raw_file'))
            },
            { silent: true }
        );
        return this;
    },

    _getCdvaFileSize: (cdvaFile) =>
        new Promise((resolve, reject) => {
            if (!cdvaFile.file) {
                resolve(0);
                return;
            }

            cdvaFile.file((file) => {
                resolve(file.size);
            }, reject);
        }),

    _process_native: async function (success, error) {
        var path = this.get_url_native();

        if (!path) {
            $f.mobile.logging.log_error('Found null in file in _process_file_for_native()');
            return error(); // skip
        }

        this.set('original_url_native', path);

        const [copyError, cdvaFile] = await copyFileToAppDirectory(path, this.get('mimetype'));

        if (copyError) {
            $f.mobile.logging.log_error('Failed to copy file to app directory', {
                path: path,
                error: JSON.stringify(copyError)
            });

            error();
            return;
        }

        if (!cdvaFile.name) {
            $f.mobile.logging.log_error('saved new image/video with undefined name.', {
                url_cdva: cdvaFile.toInternalURL(),
                url_native: cdvaFile.toURL()
            });

            cdvaFile.name = 'zenput-file.jpg'; // this should never happen, but if it does, just assume .jpg
        }

        const size = await this._getCdvaFileSize(cdvaFile);

        // we're saving url_cdva here because we'll lose access to toURL() once the FileEntry is serialized to disk
        this.set({
            url_native: cdvaFile.nativeURL,
            url_cdva: cdvaFile.toInternalURL(),
            raw_file: cdvaFile,
            size
        });

        success(this);
    }
});

export const ZenputFiles = BaseZenputCollection.extend({
    model: ZenputFile
});

/**
 * An uploadable file (image, video, signature, etc).
 * Tracks status of an upload and handles uploading.
 */
export const UploadableFile = BaseZenputModel.extend({
    defaults: {
        file: null, // ZenputFile
        filename: '',
        upload_filename: '',
        server_filename: '', // @deprecated - used by pre-direct-to-S3 app - @jira:MOBILE-660
        file_type: '',
        parent_upload_guid: '',
        response: '',
        status: null,
        upload_percentage: 0
    },

    submodels: {
        file: ZenputFile
    },

    _active_jquery_request: null,

    initialize: function (attrs, options) {
        this.on('change:upload_percentage', function () {
            showUploadProgress();
        });
    },

    cancel_upload: function () {
        if (this.get('status') === $f.mobile.uploads.upload_states.PENDING) {
            this.set('status', $f.mobile.uploads.upload_states.ERROR);
        }

        if (this._active_jquery_request) {
            this._active_jquery_request.abort();
        }
    },

    get_timeout: function () {
        return this.get('file_type').indexOf('video') > -1 ? 900000 : 90000; // 15 min for video, 1.5 min for photos
    },

    needs_upload: function () {
        return _.contains(
            [$f.mobile.uploads.upload_states.ERROR, $f.mobile.uploads.upload_states.PENDING],
            this.get('status')
        );
    },

    /**
     * Did the upload finish "successfully"? This includes uploads that failed due to the file not being on device
     * because there's nothing we can do to fix that if that's the case.
     */
    is_upload_finished: function () {
        return _.contains(
            [$f.mobile.uploads.upload_states.SUCCESS, $f.mobile.uploads.upload_states.MISSING_FILE],
            this.get('status')
        );
    },

    /**
     * Is the upload missing from device?
     */
    is_upload_missing: function () {
        return this.get('status') === $f.mobile.uploads.upload_states.MISSING_FILE;
    },

    is_upload_active: function () {
        return this.get('status') === $f.mobile.uploads.upload_states.ACTIVE;
    },

    upload: function (options) {
        options.success = options.success || function () {};
        options.error = options.error || function () {};

        if (getDebuggingFlag('fail_file_uploads')) {
            return options.error();
        }

        var this_ = this;
        var file_type = this.get('file_type');

        this.set('status', $f.mobile.uploads.upload_states.ACTIVE);

        var upload_platform = function (_success, _error) {
            if (isNative() && file_type != 'signature') {
                this_._upload_native(_success, _error, options.auth);
            } else {
                // signature (web/native) and web:
                this_._upload_web(_success, _error, options.auth);
            }
        };

        var success = function (response) {
            if (this_.get('status') !== $f.mobile.uploads.upload_states.MISSING_FILE) {
                // @author:david we could have called success() even if we failed to find the image on the device,
                // so don't override that status if it's set.
                this_.set('status', $f.mobile.uploads.upload_states.SUCCESS);
            }

            options.success(this_);
        };

        var error = function (_error) {
            this_.set('status', $f.mobile.uploads.upload_states.ERROR);
            options.error(this_, _error);
        };

        // handle web and signatures
        if (isWeb() || file_type === 'signature') {
            upload_platform(success, error);
            return;
        }

        var local_url = this.get('file').get_url_native();

        if (!local_url) {
            $f.mobile.logging.log_error('get_url_native() returned null. Skipping file...');
            return success(); // this shouldn't happen, but nothing else we can do at this point
        }

        // @note @author:david this doesn't always correctly error, so we will also ignore errors due to missing
        // files in upload_file() (@see FileTransfer()).
        CordovaPlugins().resolveLocalFileSystemURL(
            local_url,
            function (entry) {
                upload_platform(success, error);
            },
            function (_error) {
                // @author:bruce be very careful about modifying this. we don't know why, but files are getting
                // deleted randomly. We haven't been able to repro this situation without manually deleting the file, but
                // this code lets the form upload proceed even if files are deleted while the upload is in progress.
                $f.mobile.logging.log_error('Unable to resolveLocalFileSystemURL()', {
                    url_native: this_.get('file').get_url_native(),
                    error: _error
                });

                this_.set('status', $f.mobile.uploads.upload_states.MISSING_FILE);
                success();
            }
        );
    },

    _upload_native: function (success, error, auth) {
        var this_ = this;
        var mimetype = this.get('file').get('mimetype');

        // @see: https://cordova.apache.org/blog/2017/10/18/from-filetransfer-to-xhr2.html
        CordovaPlugins().resolveLocalFileSystemURL(this.get('file').get_url_native(), function (file_entry) {
            file_entry.file(function (file) {
                var reader = new FileReader();
                reader.onloadend = function () {
                    var blob = new Blob([new Uint8Array(this.result)], { type: mimetype });
                    this_._perform_xhr(blob, mimetype, auth, success, error);
                };
                reader.onerror = function () {
                    reader.abort();
                    error();
                };
                reader.readAsArrayBuffer(file);
            });
        });
    },

    _upload_web: async function (success, error, auth) {
        var file = this.get('file').get('raw_file');
        var mime_type = this.get('file').get('mimetype');

        // convert base64 string to image blob so that we can avoid
        // processing signatures on the server
        // @see https://stackoverflow.com/questions/16245767/creating-a-blob-from-a-base64-string-in-javascript
        var base64toBlob = function (base64Data, contentType) {
            contentType = contentType || '';
            var sliceSize = 1024;
            var byteCharacters = atob(base64Data);
            var bytesLength = byteCharacters.length;
            var slicesCount = Math.ceil(bytesLength / sliceSize);
            var byteArrays = new Array(slicesCount);

            for (var sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
                var begin = sliceIndex * sliceSize;
                var end = Math.min(begin + sliceSize, bytesLength);

                var bytes = new Array(end - begin);
                for (var offset = begin, i = 0; offset < end; ++i, ++offset) {
                    bytes[i] = byteCharacters[offset].charCodeAt(0);
                }
                byteArrays[sliceIndex] = new Uint8Array(bytes);
            }
            return new Blob(byteArrays, { type: contentType });
        };

        if (this.get('file_type') === 'signature') {
            return this._perform_xhr(base64toBlob(file.raw, mime_type), mime_type, auth, success, error);
        }

        // file is a serialized File object not a blob.
        file = await base64UrlToBlob(this.get('file').get('url_base64'));
        this._perform_xhr(file, mime_type, auth, success, error);
    },

    _perform_xhr: function (file_blob, mime_type, auth, success, error) {
        var this_ = this;

        this._active_jquery_request = $f.storage.perform_file_upload({
            file: file_blob,
            filename: this.get('upload_filename'),
            mime_type: mime_type,
            timeout: this.get_timeout(),
            on_progress: function (percentage) {
                this_.set('upload_percentage', percentage);
            },
            success: success,
            error: error
        });
    }
});
